import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { validateSemiForm } from 'lib/forms/formUtils'
import { isBedbankOffer, isBundleOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMinAdultAge,
  getTourV2OfferMinChildAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
  isSalesforceTourV2,
  getOfferMaxTeenagerAge,
} from 'lib/offer/offerUtils'
import { countOccupantsForProperty, countOccupantsForBundleAndSave } from 'lib/offer/occupancyUtils'
import config from 'constants/config'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { preventRenderWithClosedFilter, shouldCollectTourAdultAgesForFlights } from 'lib/offer/offerPageUtil'

interface Props {
  rooms: Array<OfferPageRoom>
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  tourVariation?: App.Tours.TourV2OfferVariation
  tourFlightsEnabled: boolean
  open?: boolean
  capacities?: Array<App.RoomCapacity>
  childrenAgeLabel?: string
  className?: string
  bundleOfferId?: string
  disableMultiRoom?: boolean
  maxRooms?: number
  /** Disable all "+" buttons to prevent adding more occupants */
  disableAddOccupants?: boolean
  /** Maximum total occupants allowed (excluding infants). When set, new rooms will adjust adults to fit within limit */
  maxOccupants?: number
}

function OfferCapacityWithAgesSelection(props: Props) {
  const {
    rooms,
    offer,
    tourVariation,
    tourFlightsEnabled,
    open,
    capacities,
    childrenAgeLabel: childrenAgeLabelProp,
    className,
    bundleOfferId,
    disableMultiRoom = !config.accommodation.allowsMultiRoom,
    maxRooms,
    disableAddOccupants,
    maxOccupants,
  } = props

  const intl = useIntl()
  const childrenAgeLabel = childrenAgeLabelProp ?? intl.formatMessage({
    id: 'EbrBVoqyE0WN72ptuE4q',
    defaultMessage: 'Age at time of check-in',
  })

  const pickerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (pickerRef.current) {
      if (open) {
        validateSemiForm(pickerRef.current)
      }
    }
  }, [open])

  const onGuestChange = useCallback((nextRooms: Array<OfferPageRoom>) => {
    if (isBedbankOffer(offer) || isTourV2Offer(offer)) {
      dispatch({ type: OfferPageActions.setRooms, rooms: nextRooms.map(room => generateOfferPageRoom(room)) })
    } else {
      dispatch({
        type: OfferPageActions.setRooms,
        rooms: nextRooms.map((room) => ({
          ...room,
          ...(isBundleOffer(offer) ? countOccupantsForBundleAndSave([room], offer) : countOccupantsForProperty([room], offer.property)),
        })),
      })
    }
  }, [offer, dispatch])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbankOffer(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      return getTourV2OfferMinAdultAge(offer, tourVariation) - 1
    } else if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer, tourVariation])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && isSalesforceTourV2(offer)), [offer])
  const tourMinChildAge = isTourV2Offer(offer) && !isLETour ? getTourV2OfferMinChildAge(offer, tourVariation) : undefined

  const infantAgeThreshold = useMemo(() => {
    if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  const teenagerAgeThreshold = useMemo(() => {
    return getOfferMaxTeenagerAge(offer)
  }, [offer])

  return (
    <OccupancyPicker
      ref={pickerRef}
      bundleOfferId={bundleOfferId}
      occupancies={rooms}
      saleUnit={offer.saleUnit}
      onChange={onGuestChange}
      maxChildAge={childrenAgeThreshold}
      minChildAge={tourMinChildAge}
      maxInfantAge={infantAgeThreshold}
      maxTeenagerAge={teenagerAgeThreshold}
      capacities={capacities}
      data-testid="offer-capacity-with-ages-selection-dropdown"
      className={className}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoom={!disableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      collectAdultAges={isTourV2Offer(offer) && shouldCollectTourAdultAgesForFlights(offer, tourFlightsEnabled, tourVariation)}
      childVariant={isLETour ? 'none' : undefined}
      offerType={offer.type}
      offerId={offer.id}
      maxRooms={maxRooms}
      disableAddOccupants={disableAddOccupants}
      maxOccupants={maxOccupants}
    />
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  bundleOfferId: state.bundleOffer?.id,
  rooms: state.rooms,
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
  tourVariation: state.tourV2Variation,
  tourFlightsEnabled: getOptimizelyFeatureFlagVariation(appState, OptimizelyFeatureFlags.flightsTourUpsellEnabled),
  open: state.activeFilter === 'capacity' || state.activeSubFilter === 'capacity' || state.activeHeaderFilter === 'capacity',
}), OfferCapacityWithAgesSelection, preventRenderWithClosedFilter)
